import React, { useEffect, useState } from 'react';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import { FaSpinner } from '@react-icons/all-files/fa/FaSpinner';
import ProfaseModal from '../commons/ProfaseModal';
import ArchivoViewerTitle from './ArchivoViewerTitle';
import { fetchProfaseArchivoContenido, type IProfaseArchivo } from './profase-archivo-api';
import './txt-viewer-modal.css';

interface TxtViewerModalProps {
  open: boolean;
  archivo: IProfaseArchivo | null;
  onClose: () => void;
  fetchContent?: () => Promise<Blob>;
  zIndex?: number;
}

export default function TxtViewerModal({ open, archivo, onClose, fetchContent, zIndex }: TxtViewerModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState('');

  useEffect(() => {
    if (!open || (!fetchContent && archivo?.id == null)) {
      setContent('');
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setContent('');

    void (async () => {
      try {
        const blob = fetchContent
          ? await fetchContent()
          : (await fetchProfaseArchivoContenido(archivo!.id!, archivo!.nombre)).blob;
        if (cancelled) {
          return;
        }
        setContent(await blob.text());
      } catch {
        if (!cancelled) {
          setError('No se pudo cargar el archivo de texto.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, archivo?.id, archivo?.nombre, fetchContent]);

  return (
    <ProfaseModal
      open={open}
      titleId="txt-viewer-modal-title"
      size="xlarge"
      scroll
      zIndex={zIndex}
      dialogClassName="txt-viewer-modal"
    >
      <div className="txt-viewer-modal__header">
        <h2 id="txt-viewer-modal-title" className="profase-modal-title m-0 txt-viewer-modal__title">
          <FaEye style={{ marginRight: 8, verticalAlign: '-2px' }} />
          <ArchivoViewerTitle archivo={archivo} fallback="Vista previa TXT" />
        </h2>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cerrar
        </button>
      </div>

      <div className="txt-viewer-modal__body">
        {loading && (
          <div className="txt-viewer-modal__status">
            <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} />
            Cargando texto…
          </div>
        )}

        {!loading && error && (
          <div className="txt-viewer-modal__error">{error}</div>
        )}

        {!loading && !error && (
          <pre className="txt-viewer-modal__content">{content || '(Archivo vacío)'}</pre>
        )}
      </div>
    </ProfaseModal>
  );
}
